import { ChevronDown, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const FAQS = [
  {
    question: "Why does Starter cost €499 per month?",
    answer: (
      <>
        Starter combines AI assistants, CRM, projects, basic finance, email and social marketing, and the Website Builder with hosting, SSL and 100 templates. It includes one workspace for up to eight users. The fee covers the plan as a whole; VAT is added where legally required.
      </>
    ),
  },
  {
    question: "What happens after the seven-day demo?",
    answer: (
      <>
        The fictional Business demo ends automatically after seven days. It does not convert into a paid plan and does not trigger an automatic charge. Using your own company data requires a separate plan purchase. See the{" "}
        <Link to="/legal/terms-of-service#fictional-demo">demo terms</Link>.
      </>
    ),
  },
  {
    question: "Can I cancel a subscription at any time?",
    answer: (
      <>
        Monthly Starter, Creator, Business and Agency subscriptions may be cancelled from Settings → Billing. Cancellation takes effect at the end of the current billing period, with access continuing until then. Partial billing periods are not refunded except where mandatory law requires otherwise. Enterprise annual agreements follow their agreed termination clause. See{" "}
        <Link to="/legal/terms-of-service#cancellation">Cancellation & refunds</Link>.
      </>
    ),
  },
  {
    question: "Does Zynthoro’s AI act without human approval?",
    answer: (
      <>
        Zynthoro’s AI specialists provide assistive output. You remain responsible for reviewing AI-generated content and independently verifying it before relying on it for legal, financial or other critical decisions. In the fictional demo, email, invoice delivery and social publishing are simulated and nothing is sent to real recipients.
      </>
    ),
  },
  {
    question: "What happens to my Kickstart licence if I upgrade?",
    answer: (
      <>
        A voluntary upgrade to Starter, Creator, Business, Agency or Enterprise replaces the Kickstart licence. Remaining included and additional Kickstart credits are not transferred or restored if the subscription later ends. The consequences must be disclosed before upgrade confirmation. Subscription AI use is included without Kickstart credit counting. See the{" "}
        <Link to="/legal/terms-of-service#plans">plan terms</Link>.
      </>
    ),
  },
  {
    question: "How does the withdrawal right apply to Kickstart?",
    answer: (
      <>
        Kickstart and credit additions are supplied digitally and made available immediately. If you purchase as a consumer, statutory withdrawal rights apply unless a valid legal exception is satisfied. Where Article 16(m) of Directive 2011/83/EU and Article 6:230p(g) of the Dutch Civil Code apply, loss of the withdrawal right requires prior express consent to immediate supply, acknowledgement of that loss and the legally required confirmation. Mandatory consumer and conformity rights remain unaffected. See the{" "}
        <Link to="/legal/terms-of-service#withdrawal-waiver">full withdrawal terms</Link>.
      </>
    ),
  },
  {
    question: "How do I move data from my current tools?",
    answer: (
      <>
        Migration options depend on your current tools and data. We&apos;ll review the available route with you during the demo call.
      </>
    ),
  },
  {
    question: "How long does setup take, and do I need an administrator?",
    answer: (
      <>
        You do not need a dedicated administrator to explore the fictional demo. Setup with your own company data depends on your configuration and migration needs.
      </>
    ),
  },
];

export default function PricingFaq() {
  return (
    <section className="zy-section bg-[#FAFAFB]" aria-labelledby="pricing-faq-title" data-testid="pricing-faq">
      <div className="max-w-4xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="zy-eyebrow mb-4">Pricing FAQ</p>
          <h2 id="pricing-faq-title" className="zy-h2">Questions before you choose a plan?</h2>
          <p className="zy-body mt-5">
            Clear answers about pricing, the seven-day demo, cancellation, AI and Kickstart.
          </p>
        </div>
        <div className="mt-9 space-y-3">
          {FAQS.map((item, index) => (
            <details
              key={item.question}
              className="group rounded-xl border border-[#e3e7ef] bg-white"
              data-testid={`pricing-faq-item-${index + 1}`}
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-left font-semibold text-[#0A1628]">
                <span>{item.question}</span>
                <ChevronDown size={18} className="shrink-0 text-[#202B60] transition-transform group-open:rotate-180" aria-hidden="true" />
              </summary>
              <div className="border-t border-[#edf0f5] px-5 py-4 text-[14px] leading-6 text-slate-600 [&_a]:font-semibold [&_a]:text-[#202B60] [&_a]:underline">
                {item.answer}
              </div>
            </details>
          ))}
        </div>
        <p className="mt-6 flex items-start gap-2 text-[12.5px] leading-5 text-slate-500">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#202B60]" aria-hidden="true" />
          This summary does not limit mandatory consumer rights. The Terms of Service govern where more detail is required.
        </p>
      </div>
    </section>
  );
}
